import { reduceMotion } from '@polestar/component-warehouse-react/helpers/accessibility';
const disableMotionOnTest = typeof navigator !== 'undefined' && navigator.userAgent === 'cw-playwright';
const defaultEasing = [0.25, 0.1, 0.25, 1];
export const MotionEasings = {
    linear: [0, 0, 1, 1],
    ease: defaultEasing,
    easeIn: [0.8, 0, 1, 1],
    easeOut: [0.15, 1, 0.35, 1],
    easeInOut: [0.65, 0, 0, 1],
    easeInOutSoft: [0.35, 0, 0, 1.0],
    default: defaultEasing, // Same as ease
};
/**
 *
 * ```js
 * import { cubicBezier } from '@polestar/component-warehouse-react';
 *
 * cubicBezier(MotionEasings.easeInOut); // cubicBezier(0.65, 0, 0, 1);
 * cubicBezier([0, 1, 1, 0]); // cubicBezier(0, 1, 1, 0);
 *
 *```
 *
 * @storybook Helpers/cubicBezier
 */
export const cubicBezier = (easing) => `cubic-bezier(${easing.join()})`;
export var MotionDurations;
(function (MotionDurations) {
    MotionDurations[MotionDurations["none"] = 0] = "none";
    MotionDurations[MotionDurations["xxSlow"] = 800] = "xxSlow";
    MotionDurations[MotionDurations["xSlow"] = 500] = "xSlow";
    MotionDurations[MotionDurations["slow"] = 400] = "slow";
    MotionDurations[MotionDurations["normal"] = 300] = "normal";
    MotionDurations[MotionDurations["fast"] = 200] = "fast";
    MotionDurations[MotionDurations["xFast"] = 100] = "xFast";
})(MotionDurations || (MotionDurations = {}));
export var MotionDurationsSeconds;
(function (MotionDurationsSeconds) {
    MotionDurationsSeconds[MotionDurationsSeconds["none"] = 0] = "none";
    MotionDurationsSeconds[MotionDurationsSeconds["xxSlow"] = 0.8] = "xxSlow";
    MotionDurationsSeconds[MotionDurationsSeconds["xSlow"] = 0.5] = "xSlow";
    MotionDurationsSeconds[MotionDurationsSeconds["slow"] = 0.4] = "slow";
    MotionDurationsSeconds[MotionDurationsSeconds["normal"] = 0.3] = "normal";
    MotionDurationsSeconds[MotionDurationsSeconds["fast"] = 0.2] = "fast";
    MotionDurationsSeconds[MotionDurationsSeconds["xFast"] = 0.1] = "xFast";
})(MotionDurationsSeconds || (MotionDurationsSeconds = {}));
export const createCustomMotionDuration = (duration) => disableMotionOnTest ? 0 : duration;
/**
 * Create a css transition which is a helper used throughout the codebase for consistent timing, etc.
 */
export const createCSSPropertyTransition = (property, 
/**
 * a combined ease and duration string like "200ms ease" when re-using the same transition configuration multiple times on different properties (color, background-color, alpha, etc);
 */
transition) => `${property} ${disableMotionOnTest ? transition.replace(/\d+ms/gim, '0ms') : transition}`;
/**
 * Create a css transition which is a helper used throughout the codebase for consistent timing, etc.
 */
export const createCSSTransition = (property, ease = '', duration = MotionDurations.normal) => `${property} ${disableMotionOnTest ? 0 : duration}ms${ease === MotionEasings.ease
    ? ''
    : ease
        ? ' ' + (typeof ease === 'string' ? ease : cubicBezier(ease))
        : ''}`;
/**
 * Create a custom framer transition.
 *
 * @returns a transition object Framer Motion understands
 */
export const createTransition = ({ duration = MotionDurationsSeconds.normal, reduced = false, disabled = false, ...transition } = {
    duration: MotionDurationsSeconds.normal,
    reduced: false,
    disabled: false,
}) => ({
    duration,
    ...transition,
    ...(disabled || (reduced && reduceMotion) ? { type: false, delay: 0 } : {}),
    ...(disableMotionOnTest
        ? {
            type: false,
            delay: 0,
        }
        : {}),
});
/**
 * Some predefined transitions used throughout the component warehouse codebase
 */
export const Transitions = {
    default: createTransition(),
    reduced: createTransition({ reduced: true }),
    largeSurface: createTransition({
        duration: 0.6,
        ease: [0.35, 0, 0, 1],
    }),
    spring: createTransition({
        duration: 1,
        type: 'spring',
        bounce: 0,
    }),
    reducedSpring: createTransition({
        duration: 1,
        reduced: true,
        type: 'spring',
        bounce: 0,
    }),
};
